import { useState, useEffect } from 'react';
import { CreateInsertionOrderRequest, InsertionOrder } from '../../../redux/billing/billing.types';
import {
  useGetInsertionOrderFieldsQuery,
  useCreateInsertionOrderMutation,
  useUpdateInsertionOrderMutation,
  useLazyGetInsertionOrderPreviewQuery,
  useSendInsertionOrderMutation,
} from '../../../redux/billing/billing.endpoints';
import { DEFAULT_BUDGET_TABLE_STRUCTURE } from '../components/insertion-order-table';
import type { IoViewMode } from '../utils/io-view-preference';

export type InsertionOrderModalMode = 'create' | 'edit' | 'view';

export type InsertionOrderFormFields = Omit<CreateInsertionOrderRequest, 'budget'>;

function signerCcOverlapMessage(ccEmails: string[], signerEmails: string[]): string | null {
  const signers = new Set(
    signerEmails.map(email => email.trim().toLowerCase()).filter(email => email.length > 0)
  );
  const overlaps: string[] = [];
  const seen = new Set<string>();
  ccEmails.forEach(email => {
    const key = email.trim().toLowerCase();
    if (!key || !signers.has(key) || seen.has(key)) return;
    seen.add(key);
    overlaps.push(email.trim());
  });
  if (overlaps.length === 0) return null;

  const alreadyReceive = 'Remove them from CC — they will already receive the document to sign.';
  if (overlaps.length === 1) {
    return `${overlaps[0]} is already a signer on this order. ${alreadyReceive}`;
  }

  const listed = overlaps.length === 2
    ? `${overlaps[0]} and ${overlaps[1]}`
    : `${overlaps.slice(0, -1).join(', ')}, and ${overlaps[overlaps.length - 1]}`;
  return `${listed} are already signers on this order. ${alreadyReceive}`;
}

const EMPTY_FORM: InsertionOrderFormFields = {
  advertiser_name: '',
  campaign_name: '',
  start_date: '',
  end_date: '',
  company_name: '',
  client_email: '',
  client_name: '',
  agent_email: '',
  agent_name: '',
  cc_emails: '',
  logo: 'invisibly',
  prebill: false,
  include_sla: true,
};

interface UseInsertionOrderFormArgs {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: InsertionOrderModalMode;
  initialData?: InsertionOrder;
  onSuccess?: () => void;
  /** Which signing provider preview and send should use. */
  provider: IoViewMode;
}

export function useInsertionOrderForm({
  open,
  onOpenChange,
  mode = 'create',
  initialData,
  onSuccess,
  provider,
}: UseInsertionOrderFormArgs) {
  const [formData, setFormData] = useState<InsertionOrderFormFields>(EMPTY_FORM);
  const [budgetData, setBudgetData] = useState<string[][]>([]);
  const [currentInsertionOrderId, setCurrentInsertionOrderId] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<InsertionOrder | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: fieldsData } = useGetInsertionOrderFieldsQuery();

  const [createInsertionOrder, { isLoading: isCreating }] = useCreateInsertionOrderMutation();
  const [updateInsertionOrder, { isLoading: isUpdating }] = useUpdateInsertionOrderMutation();
  const [sendInsertionOrder, { isLoading: isSending }] = useSendInsertionOrderMutation();
  const [triggerPreview, { isLoading: isLoadingPreview }] = useLazyGetInsertionOrderPreviewQuery();

  const isReadOnly = mode === 'view';
  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    if (initialData && open) {
      setFormData({
        advertiser_name: initialData.advertiser_name,
        campaign_name: initialData.campaign_name,
        start_date: initialData.start_date,
        end_date: initialData.end_date,
        company_name: initialData.company_name,
        client_email: initialData.client_email,
        client_name: initialData.client_name,
        agent_email: initialData.agent_email,
        agent_name: initialData.agent_name,
        cc_emails: initialData.cc_emails,
        logo: initialData.logo,
        prebill: initialData.prebill,
        include_sla: initialData.include_sla,
      });
      setBudgetData(initialData.budget || []);
    } else if (!initialData && open && mode === 'create') {
      setFormData(EMPTY_FORM);
      setBudgetData(DEFAULT_BUDGET_TABLE_STRUCTURE);
    }
    setValidationErrors({});
    if (open) {
      setCurrentInsertionOrderId(null);
    }
  }, [initialData, open, mode]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateCCEmails = (ccEmails: string): string | null => {
    if (!ccEmails.trim()) return null;
    const emails = ccEmails.split(',').map(email => email.trim()).filter(email => email.length > 0);
    const invalidEmails = emails.filter(email => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      return `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`;
    }
    if (provider !== 'boldsign') return null;
    return signerCcOverlapMessage(emails, [formData.client_email, formData.agent_email]);
  };

  const validateDates = (startDate: string, endDate: string): { start?: string; end?: string } => {
    const errors: { start?: string; end?: string } = {};
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        errors.end = 'End date cannot be before start date';
      }
    }
    return errors;
  };

  const validateField = (field: string, value: string | boolean | null): string | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const stringValue = typeof value === 'boolean' ? value.toString() : value;

    switch (field) {
      case 'client_email':
      case 'agent_email':
        if (!stringValue.trim()) return 'This field is required';
        if (!isValidEmail(stringValue)) return 'Please enter a valid email address';
        return null;
      case 'cc_emails':
        return validateCCEmails(stringValue);
      case 'advertiser_name':
      case 'campaign_name':
      case 'start_date':
      case 'end_date':
      case 'company_name':
      case 'client_name':
      case 'agent_name':
        if (!stringValue.trim()) return 'This field is required';
        return null;
      case 'logo':
      case 'prebill':
      case 'include_sla':
        return null;
      default:
        return null;
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    Object.keys(formData).forEach(field => {
      const fieldValue = formData[field as keyof typeof formData];
      if (fieldValue !== null && fieldValue !== undefined) {
        const error = validateField(field, fieldValue);
        if (error !== null && typeof error === 'string') {
          errors[field] = error;
        }
      }
    });
    const dateErrors = validateDates(formData.start_date, formData.end_date);
    if (dateErrors.start) errors.start_date = dateErrors.start;
    if (dateErrors.end) errors.end_date = dateErrors.end;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof InsertionOrderFormFields, value: string | boolean) => {
    if (isReadOnly) return;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const prepareRequestData = (): CreateInsertionOrderRequest => ({
    ...formData,
    budget: budgetData,
  });

  const saveOrUpdateInsertionOrder = async(requestData: CreateInsertionOrderRequest): Promise<string> => {
    const shouldUpdate = (mode === 'edit' && initialData?.id) || currentInsertionOrderId;

    if (shouldUpdate) {
      const idToUpdate = initialData?.id || currentInsertionOrderId!;
      await updateInsertionOrder({
        id: idToUpdate,
        data: requestData,
      }).unwrap();
      return idToUpdate;
    } else {
      const result = await createInsertionOrder({ data: requestData, provider }).unwrap();
      setCurrentInsertionOrderId(result.id);
      return result.id;
    }
  };

  const handleSave = async() => {
    if (!validateForm()) {
      return;
    }
    try {
      const requestData = prepareRequestData();
      await saveOrUpdateInsertionOrder(requestData);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save insertion order:', error);
    }
  };

  const handlePreviewAndSend = async() => {
    if (!validateForm()) {
      return;
    }
    setIsGenerating(true);
    try {
      const requestData = prepareRequestData();
      const insertionOrderId = await saveOrUpdateInsertionOrder(requestData);

      const previewResult = await triggerPreview({ id: insertionOrderId, provider }).unwrap();
      setPreviewData(previewResult);
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApproveAndSend = async() => {
    if (!previewData?.id) {
      console.error('Cannot send insertion order: missing ID');
      return;
    }

    try {
      await sendInsertionOrder({ id: previewData.id, provider }).unwrap();
      setIsPreviewModalOpen(false);
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to send insertion order:', error);
    }
  };

  const handleBackToEdit = () => {
    setIsPreviewModalOpen(false);
  };

  const handleViewDocument = () => {
    setPreviewData(initialData ?? null);
    setIsPreviewModalOpen(true);
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'Create Insertion Order';
      case 'edit': return 'Edit Insertion Order';
      case 'view': return 'View Insertion Order';
      default: return 'Insertion Order';
    }
  };

  const getSuggestions = (fieldName: string): string[] => {
    if (!fieldsData) return [];
    switch (fieldName) {
      case 'advertiser_name':
        return fieldsData.advertiser || [];
      case 'campaign_name':
        return fieldsData.campaign || [];
      case 'client_name':
        return fieldsData.billing_contact || [];
      case 'client_email':
        return fieldsData.billing_contact_email || [];
      case 'company_name':
        return (fieldsData.company_name || []).filter(Boolean) as string[];
      case 'agent_name':
        return fieldsData.agent_name || [];
      case 'agent_email':
        return fieldsData.agent_email || [];
      default:
        return [];
    }
  };

  return {
    formData,
    budgetData,
    setBudgetData,
    validationErrors,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    previewData,
    isGenerating,
    isReadOnly,
    isSaving,
    isSending,
    isLoadingPreview,
    handleInputChange,
    handleSave,
    handlePreviewAndSend,
    handleApproveAndSend,
    handleBackToEdit,
    handleViewDocument,
    getTitle,
    getSuggestions,
  };
}
